import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Waveform } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatErr } from "@/lib/api";
import GoogleLoginButton from "@/components/GoogleLoginButton";
import Logo from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      nav("/dashboard");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      {/* Side panel */}
      <div className="hidden lg:flex lg:col-span-2 bg-[--sky] text-[--ink] p-10 flex-col justify-between border-r border-[--line]">
        <Link to="/" data-testid="brand-link" className="flex items-center">
          <Logo className="h-9" />
        </Link>
        <div>
          <div className="overline text-[--accent] mb-4">AUDIO → SRT → TRANSLATE</div>
          <h2 className="font-display font-black text-4xl tracking-tighter leading-[0.95]">
            Welcome back.
            <br />
            Subtitle at speed.
          </h2>
          <p className="mt-4 text-[--ink]/70 max-w-sm leading-relaxed">
            Speech customized for your business. Pick up where you left off.
          </p>
        </div>
        <div className="font-mono text-xs text-[--muted]">Flowscribe-WEngine · Flowscribe-GPEngine · Flowscribe-EEngine · Flowscribe-GEngine · Flowscribe-AEngine</div>
      </div>

      {/* Form */}
      <div className="lg:col-span-3 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center mb-10">
            <Logo className="h-8" />
          </Link>
          <div className="overline text-[--muted] mb-4">SIGN&nbsp;IN</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-8">Enter the studio.</h1>

          <GoogleLoginButton testId="google-login-btn" />

          <div className="flex items-center gap-4 my-6">
            <div className="h-px flex-1 bg-[--line]"></div>
            <span className="overline text-[--muted]">OR</span>
            <div className="h-px flex-1 bg-[--line]"></div>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
                placeholder="you@studio.com"
              />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full bg-[--accent] text-white py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-sm text-[--muted]">
            No account?{" "}
            <Link to="/register" data-testid="go-to-register" className="text-[--ink] font-semibold underline underline-offset-4 hover:text-[--accent]">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
